import { IconDownload, IconEye, SmallButton } from 'components';
import { formatPriceWithComma, openUrlWithRole } from 'utils/helpers';
import { INVOICE_STATUS, ROLE_TYPES } from 'api/constants';
import { COLORS } from 'utils/colors';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { endpoints } from 'api/endpoints';
import { useAuth } from 'context/authContext';
import { getRequestData } from 'api/api';
import { OrangeButton } from 'components';

export const SingleInvoiceRow = ({
  invoice,
  mode = 'list',
  onPreviewClick,
  onSubmitClick,
}) => {
  const { role } = useAuth();
  const [invoiceData, setInvoiceData] = useState(invoice);

  useEffect(() => {
    setInvoiceData(invoice);
  }, [invoice]);

  const openInvoiceFile = useCallback(async () => {
    if (invoiceData.url) {
      openUrlWithRole(invoiceData.url, role || ROLE_TYPES.slh_manager);
      return;
    }

    const { data } = await getRequestData(
      endpoints.exportInvoice(invoiceData.invoice_id),
      ROLE_TYPES.slh_manager,
    );
    if (data?.url) {
      openUrlWithRole(data.url, role || ROLE_TYPES.slh_manager);
    }
  }, [invoiceData.invoice_id, invoiceData.url, role]);

  const invoiceStatus = String(invoiceData.status || '').toLowerCase();

  const renderBackendDate = (value) => {
    if (!value) return '-';
    return String(value).split('T')[0];
  };

  const storeButton = useMemo(() => {
    if (role === ROLE_TYPES.admin) {
      return (
        <a
          href={invoiceData.url}
          onClick={(e) => {
            e.preventDefault();
            openUrlWithRole(invoiceData.url, role);
          }}
          rel="noreferrer"
        >
          <SmallButton
            btnClassName="h-10"
            disabled={invoiceStatus === 'pending'}
            label="Download"
          />
        </a>
      );
    }

    if (role === ROLE_TYPES.slh_manager && mode === 'list') {
      if (invoiceStatus === 'submitted') {
        return (
          <div className="flex items-center justify-end gap-x-2">
            <IconDownload
              onClick={openInvoiceFile}
              color={COLORS.darkGrey}
              className="w-5 h-5 cursor-pointer"
            />
          </div>
        );
      }

      if (invoiceStatus === 'ready') {
        return (
          <div className="flex items-center justify-end gap-x-2">
            <IconDownload
              onClick={openInvoiceFile}
              color={COLORS.darkGrey}
              className="w-5 h-5 cursor-pointer"
            />
          </div>
        );
      }

      if (invoiceStatus === 'pending') {
        return (
          <div className="flex items-center w-full justify-between">
            <IconDownload
              onClick={openInvoiceFile}
              color={COLORS.darkGrey}
              className="w-5 h-5 cursor-pointer"
            />
            <SmallButton label="View" onClick={onPreviewClick} btnClassName="!h-8 !bg-transparent !border-0 !shadow-none !text-blue !p-0" />
            <OrangeButton
              label="Submit"
              onClick={onSubmitClick}
              btnClassName="!h-8 !text-xs !px-3"
            />
          </div>
        );
      }

      return (
        <div className="flex items-center justify-end gap-x-2">
          <SmallButton label="View" onClick={onPreviewClick} btnClassName="!h-8 !bg-transparent !border-0 !shadow-none !text-blue !p-0" />
        </div>
      );
    }

    if (role === ROLE_TYPES.slh_manager) return null;

    return (
      <div className="flex items-center justify-end gap-x-2">
        <IconEye
          onClick={onPreviewClick}
          color={COLORS.blue}
          className="bg-white p-1.5 w-8 h-8 shadow-block-shadow rounded-lg cursor-pointer"
        />
      </div>
    );
  }, [role, invoiceData, invoiceStatus, mode, onPreviewClick, onSubmitClick, openInvoiceFile]);

  return (
    <div className="flex items-center w-full border-b border-color-divider py-4">
      <p className="w-[10%] text-14 font-medium pr-2">{invoiceData.invoice_id}</p>
      <p className="w-[12%] text-14 font-medium pr-2">{renderBackendDate(invoiceData.week_start)}</p>
      <p className="w-[12%] text-14 font-medium pr-2">{renderBackendDate(invoiceData.week_end)}</p>
      <p className="w-[19%] text-14 font-medium pr-2">{invoiceData.partner.name}</p>
      <p className="w-[10%] text-14 font-medium pr-2">{invoiceData.active_clients}</p>
      <p className="w-[10%] text-14 font-medium pr-2">${formatPriceWithComma(invoiceData.total)}</p>
      <p className={`w-[9%] text-14 font-medium pr-2 ${invoiceStatus === 'pending' ? 'text-orange' : invoiceStatus === 'paid' ? 'text-green' : 'text-black'}`}>
        {invoiceStatus === 'ready' ? 'Submitted' : INVOICE_STATUS[invoiceStatus]}
      </p>
      <div className="flex items-center justify-end w-[18%]">{storeButton}</div>
    </div>
  );
};
